import {
    all,
    put,
    race,
    SagaIterator,
    select,
    take,
    takeErrorRequest,
    takeEvery,
    takeEveryCompletedRequest,
    takeFatalErrorRequest,
    takeSuccessfulRequest
} from './core/sagaEffects';
import { AccountNotificationsRequest, NotificationMarkAsReadRequest } from 'acscp/api/requests';
import { getAccountNotifications, getNumberOfUnreadNotifications } from 'acscp/store/selectors/accountNotificationSelector';
import { setAppBadgeNumber } from 'acscp/mobile/device/setAppBadgeNumber';
import { AppResumed, FetchRequest, NetworkConnected, SetOpenMenu } from 'acscp/actions';
import { AccountNotification, Menu } from 'acscp/store';
import { hasAuthenticationSelector } from 'acscp/sagas/core/selectors';

export function* accountNotificationSaga(): SagaIterator {
    yield all([
        takeEvery(SetOpenMenu, handleMenuOpened),

        takeEveryCompletedRequest(AccountNotificationsRequest, handleAccountNotificationUpdates),
        takeEveryCompletedRequest(NotificationMarkAsReadRequest, handleAccountNotificationUpdates),

        takeEvery(AppResumed, refreshNotifications),
        takeEvery(NetworkConnected, refreshNotifications),
    ]);
}

export function* handleMenuOpened(action: SetOpenMenu): SagaIterator {
    if (action.menu === Menu.AccountNotifications) {
        // Capture the notifications that are present when the menu was opened. These will be marked as read, even if
        // the menu is switched/closed or the refresh fails.
        let notificationsToMarkAsRead = (yield select(getAccountNotifications)) as unknown as AccountNotification[];

        yield* refreshNotifications();

        const {refreshSuccess} = (yield race({
            menuSwitchedOrClosed: take(SetOpenMenu),
            refreshSuccess: takeSuccessfulRequest(AccountNotificationsRequest),
            refreshError: takeErrorRequest(AccountNotificationsRequest),
            refreshFatalError: takeFatalErrorRequest(AccountNotificationsRequest),
        })) as unknown as {refreshSuccess: any};

        // There might be new notifications, which will also be marked as read (unless the menu was switched or closed
        // before we received them - see race above).
        if (!!refreshSuccess) {
            notificationsToMarkAsRead = (yield select(getAccountNotifications)) as unknown as AccountNotification[];
        }

        yield* markAccountNotificationsAsRead(notificationsToMarkAsRead);
    }
}

export function* markAccountNotificationsAsRead(accountNotifications: AccountNotification[]): SagaIterator {
    const unreadNotifications = accountNotifications.filter(n => !n.hasBeenRead);

    if (unreadNotifications.length > 0) {
        yield put(new FetchRequest(new NotificationMarkAsReadRequest(unreadNotifications.map(n => n.id))));
    }
}

export function* handleAccountNotificationUpdates(): SagaIterator {
    const unreadNumber = (yield select(getNumberOfUnreadNotifications)) as unknown as number;

    setAppBadgeNumber(unreadNumber);
}

export function* refreshNotifications(): SagaIterator {
    const hasAuthentication = (yield select(hasAuthenticationSelector)) as unknown as boolean;

    if (hasAuthentication) {
        yield put(new FetchRequest(new AccountNotificationsRequest()));
    }
}
